import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./Fire.module.css";
import useFireSmoke from "./useFireSmoke.js";
import Embers from "./Embers.jsx";

// classe de ignição + duração (ms) por id
const IGN = {
  1: { k: "igExplosao", d: 720 },
  2: { k: "igGiro", d: 1250 },
  3: { k: "igFaisca", d: 900 },
  9: { k: "igEstouro", d: 800 },
  10: { k: "igAnel", d: 820 },
};

/**
 * Fogo da tela de streak: começa apagado (fumaça), acende com a ignição
 * recebida e segue em loop "Agited Suave".
 *
 * Props:
 *   ignite:    boolean  — dispara o acender (false->true)
 *   ignition:  number   — id da ignição (1 | 2 | 3 | 9 | 10)
 *   onIgnited: fn        — chamado quando termina de acender (loop começa)
 *   size:      number    — escala
 */
export default function StreakFire({ ignite = false, ignition = 1, onIgnited, size = 1 }) {
  const [lit, setLit] = useState(false);
  const [igKey, setIgKey] = useState("");
  const stageRef = useRef(null);
  const startedRef = useRef(false);

  // fumaça enquanto apagado (não aceso e não acendendo)
  const smkRef = useFireSmoke(styles, !lit && !igKey);

  const spawnParticles = useCallback((ig) => {
    const st = stageRef.current;
    if (!st) return;

    // #2 Giro+Luz: luzinhas redondas convergindo enquanto gira
    if (ig === 2) {
      const n = 10;
      for (let i = 0; i < n; i++) {
        const ang = (i / n) * Math.PI * 2;
        const r = 46;
        const d = document.createElement("span");
        d.className = styles.convDot;
        d.style.cssText =
          `--sx:${(Math.cos(ang) * r).toFixed(1)}px;` +
          `--sy:${(Math.sin(ang) * r - 22).toFixed(1)}px;` +
          `animation-delay:${i * 60}ms`;
        st.appendChild(d);
        setTimeout(() => d.remove(), 1200);
      }
    }

    // #3 Faísca: cai de cima, deixando até 4 bolinhas amarelas na diagonal pra cima
    if (ig === 3) {
      const sp = document.createElement("span");
      sp.className = styles.descSpark;
      st.appendChild(sp);
      setTimeout(() => sp.remove(), 650);

      for (let i = 0; i < 4; i++) {
        setTimeout(() => {
          const t = document.createElement("span");
          t.className = styles.trailDot;
          const top = 14 + i * 20; // ao longo da queda
          const dir = i % 2 === 0 ? 1 : -1;
          t.style.cssText =
            `top:${top}px;` +
            `--tx:${(dir * (16 + Math.random() * 10)).toFixed(1)}px;` +
            `--ty:-${(14 + Math.random() * 10).toFixed(1)}px`;
          st.appendChild(t);
          setTimeout(() => t.remove(), 520);
        }, 80 + i * 110);
      }
    }

    // #9 Estouro: faíscas voando pra fora logo após o flash
    if (ig === 9) {
      setTimeout(() => {
        for (let i = 0; i < 12; i++) {
          const a = Math.random() * Math.PI * 2;
          const dist = 28 + Math.random() * 42;
          const s = document.createElement("span");
          s.className = styles.burstSpark;
          s.style.cssText =
            `--ex:${(Math.cos(a) * dist).toFixed(1)}px;` +
            `--ey:${(Math.sin(a) * dist - 18).toFixed(1)}px;` +
            `animation-delay:${Math.floor(Math.random() * 90)}ms`;
          st.appendChild(s);
          setTimeout(() => s.remove(), 800);
        }
      }, 140);
    }
    // #10 Anel é puro CSS (.igAnel .ring)
  }, []);

  useEffect(() => {
    if (!ignite || startedRef.current) return;
    startedRef.current = true;

    const cfg = IGN[ignition] || IGN[1];
    setIgKey(cfg.k);
    spawnParticles(ignition);

    const t = setTimeout(() => {
      setIgKey("");
      setLit(true);
      onIgnited && onIgnited();
    }, cfg.d);

    return () => clearTimeout(t);
  }, [ignite, ignition, onIgnited, spawnParticles]);

  const cls = [
    styles.fc,
    styles.vAgited,
    lit ? styles.lit : "",
    igKey ? styles.animating : "",
    igKey ? styles[igKey] : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls} style={{ "--fire-scale": size }}>
      <div className={styles.stage} ref={stageRef}>
        <div className={styles.glow} />
        <div className={styles.ring} />
        <div className={styles.smk} ref={smkRef} />
        <div className={styles.fw}>
          <div className={`${styles.fl} ${styles.or}`} />
          <div className={`${styles.fl} ${styles.gd}`} />
          <div className={`${styles.fl} ${styles.wh}`} />
        </div>
        <Embers />
      </div>
    </div>
  );
}